import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import attendanceAPI from '../api/attendanceAPI';
import { hasCameraMediaRole } from '../utils/cameraBookingUtils';

const AttendanceAuthContext = createContext(null);

const STORAGE_KEY = 'attendance_user';
const TOKEN_KEY = 'attendance_token';

/**
 * Attendance Auth Context Provider
 * Manages attendance session state separately from main app auth
 */
export function AttendanceAuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  // Expired API tokens must end the whole shared BU Dashboard session. Without
  // this, protected pages turn a 401 response into a misleading empty-data error.
  useEffect(() => {
    const handleUnauthorized = () => {
      setUser(null);
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem('ea_user');
      localStorage.removeItem('ea_token');
      localStorage.removeItem('ea_hr_token');
      attendanceAPI.setDefaultFacultyId(null);
      navigate('/bu-dashboard/login', { replace: true });
    };
    window.addEventListener('attendance-unauthorized', handleUnauthorized);
    return () => window.removeEventListener('attendance-unauthorized', handleUnauthorized);
  }, [navigate]);

  // Keep this compatibility context synchronized with the main BU Dashboard login.
  useEffect(() => {
    const syncSession = (event) => {
      const detail = event?.detail || {};
      if (!detail.token || !detail.user) return;
      let existingUser = {};
      try {
        existingUser = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
      } catch {
        existingUser = {};
      }
      const faculties = detail.user.faculties?.length
        ? detail.user.faculties
        : existingUser.faculties || [];
      const existingFaculty = faculties.find(
        (item) => Number(item.id) === Number(existingUser?.faculty?.id)
      );
      const userData = {
        ...existingUser,
        ...detail.user,
        roles: detail.user.roles || [],
        is_tutor: Boolean(detail.user.is_tutor),
        is_faculty_admin: Boolean(detail.user.is_faculty_admin),
        is_camera_media: Boolean(detail.user.is_camera_media),
        faculties,
        faculty: existingFaculty || detail.user.faculty || faculties[0] || null,
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(userData));
      localStorage.setItem(TOKEN_KEY, detail.token);
      setUser(userData);
      setError(null);
      attendanceAPI.setDefaultFacultyId(
        userData?.faculty?.id || userData?.faculty_id || null
      );
    };
    window.addEventListener('attendance-session-sync', syncSession);
    return () => window.removeEventListener('attendance-session-sync', syncSession);
  }, []);

  // Load user and token from localStorage on mount
  useEffect(() => {
    const storedUser =
      localStorage.getItem(STORAGE_KEY) || localStorage.getItem('ea_user');
    const storedToken =
      localStorage.getItem(TOKEN_KEY) ||
      localStorage.getItem('ea_token') ||
      localStorage.getItem('ea_hr_token');
    
    if (storedUser && storedToken) {
      try {
        const userData = JSON.parse(storedUser);
        // Check if token is expired
        if (userData.expires_at) {
          const expiresAt = new Date(userData.expires_at);
          const now = new Date();
          if (expiresAt <= now) {
            window.dispatchEvent(new CustomEvent('attendance-unauthorized'));
            setLoading(false);
            return;
          }
        }
        setUser(userData);
        attendanceAPI.setDefaultFacultyId(userData?.faculty?.id || userData?.faculty_id || null);
      } catch (e) {
        console.error('Failed to parse stored user:', e);
        localStorage.removeItem(STORAGE_KEY);
        localStorage.removeItem(TOKEN_KEY);
      }
    }
    setLoading(false);
  }, []);

  // Always refresh the managed-faculty list from Odoo. This also repairs older
  // browser sessions that stored only the primary faculty.
  useEffect(() => {
    if (!user?.is_faculty_admin) return undefined;
    let cancelled = false;

    const refreshFaculties = async () => {
      const token = localStorage.getItem(TOKEN_KEY) || localStorage.getItem('ea_token');
      if (!token) return;
      try {
        const result = await attendanceAPI.getFaculties(token);
        const faculties = result?.faculties || [];
        if (cancelled || !faculties.length) return;
        setUser((current) => {
          const selected = faculties.find(
            (item) => Number(item.id) === Number(current?.faculty?.id || current?.faculty_id)
          ) || faculties[0];
          const updated = {
            ...current,
            faculties,
            faculty: selected,
            faculty_id: selected.id,
          };
          localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
          attendanceAPI.setDefaultFacultyId(selected.id);
          return updated;
        });
      } catch (err) {
        console.error('Failed to refresh managed faculties:', err);
      }
    };

    refreshFaculties();
    return () => {
      cancelled = true;
    };
  }, [user?.is_faculty_admin]);

  /**
   * Login user and store session info
   */
  const login = async (username, password) => {
    try {
      setLoading(true);
      setError(null);

      console.log('Calling attendanceAPI.login with:', username); // Debug log
      const result = await attendanceAPI.login(username, password);
      console.log('Login API result:', result); // Debug log
      console.log('Result success:', result?.success); // Debug log
      console.log('Result keys:', result ? Object.keys(result) : 'null'); // Debug log

      if (result && result.success === true) {
        // Store token securely
        const token = result.token;
        if (!token) {
          console.error('No token in result:', result); // Debug log
          throw new Error('No token received from server');
        }
        
        const userData = {
          name: result.employee?.name || result.tutor?.name,
          email: result.employee?.email || result.tutor?.email,
          employee_id: result.employee?.id || result.tutor?.id,
          user_id: result.employee?.user_id || result.tutor?.user_id,
          roles: result.roles || [],
          is_tutor: result.is_tutor || false,
          is_faculty_admin: result.is_faculty_admin || (result.roles || []).includes('faculty_admin'),
          is_camera_media: result.is_camera_media || (result.roles || []).includes('camera_media'),
          tutor: result.tutor || null,
          faculty: result.faculty || null,
          faculties: result.faculties || (result.faculty ? [result.faculty] : []),
          expires_at: result.expires_at || null
        };

        console.log('User data to store:', userData); // Debug log
        setUser(userData);
        attendanceAPI.setDefaultFacultyId(userData?.faculty?.id || userData?.faculty_id || null);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(userData));
        localStorage.setItem(TOKEN_KEY, token);

        return { success: true, user: userData, token: token };
      } else {
        // Check if result has error or message
        // Prefer message over error type for better user experience
        const errorMsg = result?.message || result?.error || 'Login failed';
        console.error('Login failed - error:', errorMsg, 'Full result:', result); // Debug log
        setError(errorMsg);
        return { success: false, error: errorMsg };
      }
    } catch (err) {
      console.error('Login exception:', err); // Debug log
      const errorMsg = err.message || 'Network error. Please check your connection.';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
    }
  };

  /**
   * Logout user and clear token
   */
  const logout = async () => {
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      if (token) {
        await attendanceAPI.logout(token);
      }
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      setUser(null);
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(TOKEN_KEY);
      attendanceAPI.setDefaultFacultyId(null);
      setError(null);
      navigate('/attendance/login');
    }
  };

  /**
   * Select one of the faculties assigned to this administrator.
   * The API client automatically scopes all faculty-admin requests to it.
   */
  const selectFaculty = (facultyId) => {
    const id = Number(facultyId);
    const faculties = user?.faculties || [];
    const faculty = faculties.find((item) => Number(item.id) === id);
    if (!faculty) return false;

    const userData = { ...user, faculty, faculty_id: faculty.id };
    setUser(userData);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(userData));
    try {
      const mainUser = JSON.parse(localStorage.getItem('ea_user') || '{}');
      localStorage.setItem('ea_user', JSON.stringify({
        ...mainUser,
        faculties,
        faculty,
        faculty_id: faculty.id,
      }));
    } catch {
      // The attendance session remains authoritative if the legacy cache is invalid.
    }
    attendanceAPI.setDefaultFacultyId(faculty.id);
    return true;
  };

  /**
   * Get current token
   */
  const getToken = () => {
    return localStorage.getItem(TOKEN_KEY);
  };

  /**
   * Check if token is expired
   */
  const isTokenExpired = () => {
    if (!user?.expires_at) return false;
    const expiresAt = new Date(user.expires_at);
    const now = new Date();
    return expiresAt <= now;
  };

  /**
   * Check if session is still valid
   */
  const checkSession = async () => {
    try {
      const token = getToken();
      if (!token) {
        return false;
      }
      
      // Check if token is expired
      if (isTokenExpired()) {
        setUser(null);
        localStorage.removeItem(STORAGE_KEY);
        localStorage.removeItem(TOKEN_KEY);
        return false;
      }
      
      const result = await attendanceAPI.ping(token);
      return result.success === true;
    } catch (err) {
      // Token expired or invalid
      setUser(null);
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(TOKEN_KEY);
      return false;
    }
  };

  /**
   * Clear error state
   */
  const clearError = () => {
    setError(null);
  };

  const value = {
    user,
    loading,
    error,
    login,
    logout,
    checkSession,
    clearError,
    selectFaculty,
    getToken,
    isTokenExpired,
    isAuthenticated: !!user && !!getToken(),
    isTutor: user?.is_tutor || false,
    isFacultyAdmin: user?.is_faculty_admin || false,
    isCameraMedia: hasCameraMediaRole(user),
    hasBothRoles: (user?.is_tutor && user?.is_faculty_admin) || false
  };

  return (
    <AttendanceAuthContext.Provider value={value}>
      {children}
    </AttendanceAuthContext.Provider>
  );
}

/**
 * Hook to use attendance auth context
 */
export function useAttendanceAuth() {
  const context = useContext(AttendanceAuthContext);
  if (!context) {
    throw new Error('useAttendanceAuth must be used within AttendanceAuthProvider');
  }
  return context;
}

export default AttendanceAuthContext;

